import api from './api';

/**
 * Proposal Service
 * Frontend service for contractor proposal management
 */

const PROPOSALS_KEY = 'eqaya_proposals';
const USE_API = process.env.REACT_APP_USE_API !== 'false';

const apiClient = api;

const delay = () => new Promise(resolve => setTimeout(resolve, 300));

// Utility functions for localStorage
const getProposals = () => {
  const proposals = localStorage.getItem(PROPOSALS_KEY);
  return proposals ? JSON.parse(proposals) : [];
};

const proposalService = {
  /**
   * Create a new proposal
   * @param {Object} proposalData - Proposal details
   * @returns {Promise<Object>} Created proposal
   */
  async createProposal(proposalData) {
    try {
      const response = await api.post('/proposals', proposalData);
      return response.data;
    } catch (error) {
      console.error('Create proposal error:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Get proposal by ID
   * @param {string} proposalId - Proposal ID
   * @returns {Promise<Object>} Proposal details
   */
  async getProposal(proposalId) {
    try {
      const response = await api.get(`/proposals/${proposalId}`);
      return response.data;
    } catch (error) {
      console.error('Get proposal error:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Get all proposals for a project
   * @param {string} projectId - Project ID
   * @returns {Promise<Array>} List of proposals
   */
  async getProposalsForProject(projectId) {
    try {
      const response = await api.get(`/proposals/project/${projectId}`);
      return response.data;
    } catch (error) {
      console.error('Get project proposals error:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Get all proposals by current contractor
   * @returns {Promise<Array>} List of proposals
   */
  async getMyProposals() {
    await delay();
    if (!USE_API) {
      return {
        success: true,
        data: getProposals()
      };
    }

    try {
      const response = await api.get('/proposals/contractor/me');
      return response.data;
    } catch (error) {
      console.error('Get my proposals error:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Update proposal
   * @param {string} proposalId - Proposal ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated proposal
   */
  async updateProposal(proposalId, updates) {
    try {
      const response = await api.put(`/proposals/${proposalId}`, updates);
      return response.data;
    } catch (error) {
      console.error('Update proposal error:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Submit proposal for client review
   * @param {string} proposalId - Proposal ID
   * @returns {Promise<Object>} Updated proposal
   */
  async submitProposal(proposalId) {
    try {
      const response = await api.post(`/proposals/${proposalId}/submit`);
      return response.data;
    } catch (error) {
      console.error('Submit proposal error:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Accept proposal (client action)
   * @param {string} proposalId - Proposal ID
   * @returns {Promise<Object>} Updated proposal
   */
  async acceptProposal(proposalId) {
    try {
      const response = await api.post(`/proposals/${proposalId}/accept`);
      return response.data;
    } catch (error) {
      console.error('Accept proposal error:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Reject proposal (client action)
   * @param {string} proposalId - Proposal ID
   * @param {string} reason - Rejection reason
   * @returns {Promise<Object>} Updated proposal
   */
  async rejectProposal(proposalId, reason) {
    try {
      const response = await api.post(`/proposals/${proposalId}/reject`, { reason });
      return response.data;
    } catch (error) {
      console.error('Reject proposal error:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Request changes to proposal (client action)
   * @param {string} proposalId - Proposal ID
   * @param {Object} changeRequest - Requested changes
   * @returns {Promise<Object>} Updated proposal
   */
  async requestChanges(proposalId, changeRequest) {
    try {
      const response = await api.post(`/proposals/${proposalId}/request-changes`, changeRequest);
      return response.data;
    } catch (error) {
      console.error('Request changes error:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Generate AI-assisted milestones
   * @param {Object} projectData - Project information
   * @returns {Promise<Array>} Suggested milestones
   */
  async generateMilestones(projectData) {
    try {
      const response = await api.post('/proposals/generate-milestones', projectData);
      return response.data;
    } catch (error) {
      console.error('Generate milestones error:', error);
      // Return fallback milestones
      return {
        success: true,
        data: {
          milestones: this.getDefaultMilestones(projectData.projectType)
        }
      };
    }
  },

  /**
   * Validate milestone structure
   * @param {Array} milestones - Milestones to validate
   * @returns {Promise<Object>} Validation result
   */
  async validateMilestones(milestones) {
    try {
      const response = await api.post('/proposals/validate-milestones', { milestones });
      return response.data;
    } catch (error) {
      console.error('Validate milestones error:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Get contractor proposal statistics
   * @returns {Promise<Object>} Statistics
   */
  async getMyProposalStats() {
    try {
      const response = await api.get('/proposals/contractor/me/stats');
      return response.data;
    } catch (error) {
      console.error('Get proposal stats error:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Get default milestone templates (fallback)
   * @param {string} projectType - Type of project
   * @returns {Array} Default milestones
   */
  getDefaultMilestones(projectType) {
    const templates = {
      'new-construction': [
        { 
          name: 'Site Preparation & Foundation', 
          description: 'Clear site, excavate, and pour foundation',
          duration: 2, 
          durationUnit: 'weeks',
          paymentPercentage: 15,
          deliverables: ['Site cleared', 'Foundation poured and cured']
        },
        { 
          name: 'Framing & Structural Work', 
          description: 'Build structural frame and install roof trusses',
          duration: 3, 
          durationUnit: 'weeks',
          paymentPercentage: 20,
          deliverables: ['Frame complete', 'Roof structure installed']
        },
        { 
          name: 'Roofing & Exterior', 
          description: 'Install roofing, siding, windows, and doors',
          duration: 2, 
          durationUnit: 'weeks',
          paymentPercentage: 15,
          deliverables: ['Roof complete', 'Exterior weatherproof']
        },
        { 
          name: 'Plumbing & Electrical Rough-In', 
          description: 'Install plumbing and electrical systems',
          duration: 2, 
          durationUnit: 'weeks',
          paymentPercentage: 15,
          deliverables: ['Plumbing rough-in complete', 'Electrical rough-in complete']
        },
        { 
          name: 'Insulation & Drywall', 
          description: 'Install insulation and hang drywall',
          duration: 2, 
          durationUnit: 'weeks',
          paymentPercentage: 10,
          deliverables: ['Insulation installed', 'Drywall hung and taped']
        },
        { 
          name: 'Interior Finishing', 
          description: 'Paint, flooring, fixtures, and trim work',
          duration: 3, 
          durationUnit: 'weeks',
          paymentPercentage: 15,
          deliverables: ['Paint complete', 'Flooring installed', 'Fixtures installed']
        },
        { 
          name: 'Final Inspection & Handover', 
          description: 'Final walkthrough, punch list, and project handover',
          duration: 1, 
          durationUnit: 'weeks',
          paymentPercentage: 10,
          deliverables: ['Final inspection passed', 'Punch list complete', 'Keys handed over']
        }
      ],
      'renovation': [
        { 
          name: 'Demolition & Site Prep', 
          description: 'Remove old materials and prepare work area',
          duration: 1, 
          durationUnit: 'weeks',
          paymentPercentage: 10,
          deliverables: ['Demolition complete', 'Site prepared']
        },
        { 
          name: 'Structural Modifications', 
          description: 'Make structural changes as needed',
          duration: 2, 
          durationUnit: 'weeks',
          paymentPercentage: 25,
          deliverables: ['Structural work complete', 'Inspections passed']
        },
        { 
          name: 'Systems Installation', 
          description: 'Install or update plumbing, electrical, HVAC',
          duration: 2, 
          durationUnit: 'weeks',
          paymentPercentage: 25,
          deliverables: ['Systems installed', 'Systems tested']
        },
        { 
          name: 'Finishing Work', 
          description: 'Complete interior and exterior finishing',
          duration: 2, 
          durationUnit: 'weeks',
          paymentPercentage: 30,
          deliverables: ['Finishing complete', 'Quality check passed']
        },
        { 
          name: 'Final Touches & Cleanup', 
          description: 'Final details and thorough cleanup',
          duration: 1, 
          durationUnit: 'weeks',
          paymentPercentage: 10,
          deliverables: ['Cleanup complete', 'Final walkthrough approved']
        }
      ],
      'quick-task': [
        { 
          name: 'Initial Assessment', 
          description: 'Assess the work required and gather materials',
          duration: 0.5, 
          durationUnit: 'days',
          paymentPercentage: 20,
          deliverables: ['Assessment complete', 'Materials ready']
        },
        { 
          name: 'Work Execution', 
          description: 'Complete the requested work',
          duration: 1, 
          durationUnit: 'days',
          paymentPercentage: 60,
          deliverables: ['Work complete', 'Quality check passed']
        },
        { 
          name: 'Final Inspection', 
          description: 'Final review and cleanup',
          duration: 0.5, 
          durationUnit: 'days',
          paymentPercentage: 20,
          deliverables: ['Inspection passed', 'Cleanup complete']
        }
      ]
    };
    
    return templates[projectType] || templates['quick-task'];
  },

  // Get contractor statistics
  getStats: async () => {
    await delay();
    if (USE_API) {
      const { data } = await apiClient.get('/proposals/stats');
      return data;
    }
    // Local storage fallback
    const proposals = getProposals();
    const totalProposals = proposals.length;
    const acceptedProposals = proposals.filter(p => p.status === 'accepted').length;
    const pendingProposals = proposals.filter(p => p.status === 'pending').length;
    const rejectedProposals = proposals.filter(p => p.status === 'rejected').length;
    
    return {
      success: true,
      data: {
        totalProposals,
        acceptedProposals,
        pendingProposals,
        rejectedProposals,
        winRate: totalProposals > 0 ? Math.round((acceptedProposals / totalProposals) * 100) : 0,
        averageValue: proposals.length > 0 
          ? proposals.reduce((sum, p) => sum + (p.totalCost || 0), 0) / proposals.length 
          : 0
      }
    };
  }
};

export default proposalService;
